import React, { useEffect, useState } from 'react';
import { Currency } from '../types';
import { getExchangeRates, fetchLiveRates, getRateStatus, subscribeToRates, formatRate } from '../utils/currency';
import { Card, CardContent } from './ui/Card';
import { ArrowRightLeft, RefreshCw, Wifi, WifiOff, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { Input, Select } from './ui/Forms';
import { CURRENCIES, getCurrencyInfo } from '../utils/countries';
interface ExchangeRateWidgetProps {
  baseCurrency: Currency;
}
export function ExchangeRateWidget({
  baseCurrency
}: ExchangeRateWidgetProps) {
  const [, forceUpdate] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [converterAmount, setConverterAmount] = useState<number>(1000);
  const [converterFrom, setConverterFrom] = useState<Currency>(baseCurrency);
  useEffect(() => {
    fetchLiveRates();
    const unsub = subscribeToRates(() => forceUpdate(n => n + 1));
    return unsub;
  }, []);
  useEffect(() => {
    setConverterFrom(baseCurrency);
  }, [baseCurrency]);
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchLiveRates();
    setIsRefreshing(false);
  };
  const rates = getExchangeRates();
  const {
    isLive,
    lastFetched,
    status
  } = getRateStatus();
  // Calculate cross rates from base currency
  const getCrossRate = (from: Currency, to: Currency): number => {
    if (from === to) return 1;
    const fromUsd = 1 / rates[from];
    return fromUsd * rates[to];
  };
  return <Card data-id="element-181">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-182">
        <div className="flex items-center gap-3" data-id="element-183">
          <div className="bg-cyan-600 p-2 rounded-lg" data-id="element-184">
            <ArrowRightLeft className="w-4 h-4 text-white" data-id="element-185" />
          </div>
          <div data-id="element-186">
            <h3 className="text-base font-semibold text-slate-800" data-id="element-187">
              Exchange Rates
            </h3>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-188">
              {isLive ? <span className="flex items-center gap-1 text-xs text-emerald-600" data-id="element-189">
                  <Wifi className="w-3 h-3" data-id="element-190" /> Live rates
                </span> : status === 'loading' ? <span className="flex items-center gap-1 text-xs text-amber-600" data-id="element-191">
                  <RefreshCw className="w-3 h-3 animate-spin" data-id="element-192" /> Loading...
                </span> : <span className="flex items-center gap-1 text-xs text-slate-400" data-id="element-193">
                  <WifiOff className="w-3 h-3" data-id="element-194" /> Fallback rates
                </span>}
              {lastFetched && <span className="flex items-center gap-1 text-xs text-slate-400" data-id="element-195">
                  <Clock className="w-3 h-3" data-id="element-196" />
                  {lastFetched.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </span>}
            </div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isRefreshing} className="text-slate-500 hover:text-cyan-600 px-2" data-id="element-197">
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} data-id="element-198" />
        </Button>
      </div>

      <CardContent className="p-5 space-y-5" data-id="element-199">
        {/* Rate Matrix */}
        <div className="overflow-x-auto" data-id="element-200">
          <table className="w-full text-sm" data-id="element-201">
            <thead data-id="element-202">
              <tr data-id="element-203">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-204">
                  From ↓ / To →
                </th>
                {CURRENCIES.slice(0, 6).map(c => <th key={c.code} className={`text-center py-2 px-3 text-xs font-semibold uppercase tracking-wider ${c.code === baseCurrency ? 'text-cyan-700 bg-cyan-50 rounded-t-lg' : 'text-slate-400'}`} data-id="element-205">
                    <span className="mr-1" data-id="element-206">{c.flag}</span>
                    {c.code}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-207">
              {CURRENCIES.slice(0, 6).map(from => <tr key={from.code} className={from.code === baseCurrency ? 'bg-cyan-50/50' : ''} data-id="element-208">
                  <td className={`py-2.5 px-3 font-medium ${from.code === baseCurrency ? 'text-cyan-800' : 'text-slate-700'}`} data-id="element-209">
                    <span className="mr-1.5" data-id="element-210">{from.flag}</span>
                    <span className="font-semibold" data-id="element-211">{from.code}</span>
                    <span className="text-slate-400 font-normal ml-1.5 hidden sm:inline text-xs" data-id="element-212">
                      {from.name}
                    </span>
                  </td>
                  {CURRENCIES.slice(0, 6).map(to => <td key={to.code} className={`text-center py-2.5 px-3 tabular-nums ${from.code === to.code ? 'text-slate-300 font-normal' : from.code === baseCurrency || to.code === baseCurrency ? 'text-cyan-800 font-semibold' : 'text-slate-600 font-medium'} ${to.code === baseCurrency ? 'bg-cyan-50/50' : ''}`} data-id="element-213">
                      {from.code === to.code ? '—' : formatRate(getCrossRate(from.code, to.code))}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Quick Converter */}
        <div className="border-t border-slate-100 pt-4" data-id="element-214">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-215">
            Quick Convert
          </p>
          <div className="flex items-center gap-3 flex-wrap" data-id="element-216">
            <div className="flex items-center gap-2" data-id="element-217">
              <Input type="number" min="0" step="100" value={converterAmount} onChange={e => setConverterAmount(Number(e.target.value))} className="w-28 h-9 text-sm" data-id="element-218" />
              <div className="w-32 z-10" data-id="element-219">
                <Select searchable value={converterFrom} onChange={e => setConverterFrom(e.target.value as Currency)} className="h-9 text-sm" options={CURRENCIES.map(c => ({
                value: c.code,
                label: `${c.flag} ${c.code}`
              }))} data-id="element-220" />
              </div>
            </div>
            <ArrowRightLeft className="w-4 h-4 text-slate-300 shrink-0" data-id="element-221" />
            <div className="flex flex-wrap gap-2" data-id="element-222">
              {CURRENCIES.slice(0, 4).filter(c => c.code !== converterFrom).map(to => {
              const converted = converterAmount * getCrossRate(converterFrom, to.code);
              return <div key={to.code} className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-223">
                      <span className="text-sm" data-id="element-224">{to.flag}</span>
                      <span className="font-semibold text-slate-800 text-sm tabular-nums" data-id="element-225">
                        {to.symbol}
                        {converted.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })}
                      </span>
                      <span className="text-xs text-slate-400" data-id="element-226">{to.code}</span>
                    </div>;
            })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}